<script setup>
defineOptions({ inheritAttrs: false })
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Dialog, Alert } from 'frappe-ui'
import { useProductFeed } from '../composables/useProductFeed'
import { session } from '../session'

const router = useRouter()
const { showTerms, announcements, acceptTerms, tenant, query } = useProductFeed()

const termsOpen = ref(false)
const bannerDismissed = ref(false)
const accepting = ref(false)

const termsHtml = computed(
  () => query.data.value?.terms?.active?.content || '<p>No terms published.</p>',
)

watch(
  showTerms,
  (v) => {
    if (v && session.boot?.authenticated) termsOpen.value = true
  },
  { immediate: true },
)

const banner = computed(() => {
  if (bannerDismissed.value) return null
  const a = announcements.value?.[0]
  if (a) {
    return {
      title: a.title,
      body: '',
      link: a.link_url || '/whats-new',
    }
  }
  return null
})

const suspended = computed(() => {
  const fromFeed = tenant.value?.account_status
  const fromBoot = session.boot?.tenant?.account_status
  const s = fromFeed || fromBoot
  return s === 'Suspended' || s === 'Deactivated' || session.boot?.tenant?.restricted
})

const trialBanner = computed(() => {
  if (bannerDismissed.value) return null
  const t = tenant.value || session.boot?.tenant
  if (!t || t.account_status !== 'Trial') return null
  return {
    title: 'Trial account',
    body: t.trial_ends_on ? `Trial ends on ${t.trial_ends_on}.` : 'You are on a trial.',
  }
})

async function onAcceptTerms() {
  accepting.value = true
  try {
    await acceptTerms()
    termsOpen.value = false
  } finally {
    accepting.value = false
  }
}
</script>

<template>
  <div v-if="session.boot?.authenticated" v-bind="$attrs" class="space-y-2">
    <Alert v-if="suspended" title="Account restricted" theme="red" variant="subtle">
      This parish account is {{ tenant?.account_status || session.boot?.tenant?.account_status }}.
      {{ tenant?.suspension_reason || session.boot?.tenant?.suspension_reason || 'Contact your system administrator.' }}
    </Alert>

    <div
      v-if="trialBanner"
      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
    >
      <div class="min-w-0">
        <strong>{{ trialBanner.title }}</strong>
        <span class="opacity-90"> — {{ trialBanner.body }}</span>
      </div>
    </div>

    <div
      v-if="banner"
      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline-gray-2 bg-surface-gray-1 px-3 py-2 text-sm"
    >
      <div class="min-w-0">
        <strong>{{ banner.title }}</strong>
        <span v-if="banner.body" class="text-ink-gray-6"> — {{ banner.body }}</span>
      </div>
      <div class="flex gap-2">
        <Button size="sm" variant="subtle" label="View" @click="router.push(banner.link || '/whats-new')" />
        <Button size="sm" variant="ghost" label="Dismiss" @click="bannerDismissed = true" />
      </div>
    </div>
  </div>

  <Dialog v-model="termsOpen" title="Terms &amp; Conditions" size="2xl">
    <template #default>
      <div class="prose prose-sm max-h-[50vh] max-w-none overflow-y-auto" v-html="termsHtml" />
    </template>
    <template #actions>
      <Button variant="solid" label="I accept" :loading="accepting" @click="onAcceptTerms" />
    </template>
  </Dialog>
</template>
